import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn, formatCents } from '@/lib/utils';
import { LineItemDisplay } from './LineItemDisplay';
import { JustificationFooter } from './JustificationFooter';
import { EditJustificationDialog } from '../dialogs/EditJustificationDialog';
import { useLineItemCalculations, useJustificationSave, useJustificationUpsert, useJustificationDelete } from '@/hooks/useSupplementHelpers';
import { useSupplementCanWrite } from '../supplement-access-context';
import type { DisplayLineItem } from '@/types/supplement';

interface SupplementLineItemCardProps {
  item: DisplayLineItem;
  supplementId: number;
}

const CATEGORY_CONFIG = {
  unmatched_contractor: {
    label: 'Not on Insurance',
    borderColor: 'border-l-red-500',
    bgColor: 'bg-red-50/50 dark:bg-red-950/20',
  },
  underscoped: {
    label: 'Price Discrepancy',
    borderColor: 'border-l-orange-500',
    bgColor: 'bg-orange-50/50 dark:bg-orange-950/20',
  },
  overscoped: {
    label: 'Overscoped',
    borderColor: 'border-l-purple-500',
    bgColor: 'bg-purple-50/50 dark:bg-purple-950/20',
  },
  unmatched_insurer: {
    label: 'Free Scoped',
    borderColor: 'border-l-blue-500',
    bgColor: 'bg-blue-50/50 dark:bg-blue-950/20',
  },
  perfect_match: {
    label: 'Perfect Match',
    borderColor: 'border-l-green-500',
    bgColor: 'bg-green-50/50 dark:bg-green-950/20',
  },
} as const;

export function SupplementLineItemCard({ item, supplementId }: SupplementLineItemCardProps) {
  const config = CATEGORY_CONFIG[item.category];
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const canWrite = useSupplementCanWrite();

  // Extract calculations to hook
  const { diffCents, isUnmatched, hasContractorOnly } = useLineItemCalculations(item);
  
  // Extract justification save logic to hook
  const { saveJustification } = useJustificationSave(
    supplementId,
    () => setIsEditDialogOpen(false)
  );

  // Upsert hook for items that can have justifications (underscoped and unmatched_contractor)
  // Use the lineItemMatchGroupId from the item (for matched items like underscoped)
  // or lineItemId for unmatched items
  const { upsertJustification } = useJustificationUpsert(
    supplementId,
    item.lineItemMatchGroupId || null,
    item.lineItemId || null,
    () => setIsEditDialogOpen(false)
  );

  // Extract justification delete logic to hook
  const { deleteJustification } = useJustificationDelete(supplementId);

  // Wrapper function to match the dialog's expected signature
  const handleSave = (data: {
    justificationText: string;
    codeBookTitle?: string;
    chapter?: string;
    snippet?: string;
  }) => {
    if (!canWrite) return;
    // For items that can have justifications (underscoped and unmatched_contractor),
    // use upsert if justification doesn't exist, otherwise use regular update
    const canHaveJustification = item.category === 'underscoped' || item.category === 'unmatched_contractor';
    
    if (canHaveJustification && !item.justification?.id) {
      // No existing justification - use upsert to create it
      upsertJustification(data);
      return;
    }

    // Existing justification - use regular update
    if (!item.justification?.id) {
      console.error('Cannot save: missing justification ID');
      return;
    }
    saveJustification(item.justification.id, data);
  };

  // Handler for delete
  const handleDelete = () => {
    if (!canWrite) return;
    if (!item.justification?.id) {
      console.error('Cannot delete: missing justification ID');
      return;
    }
    deleteJustification(item.justification.id);
  };

  // Unmatched Item Display - Single column layout
  if (isUnmatched) {
    const items = hasContractorOnly ? item.contractorItems : item.insurerItems;

    return (
      <Card
        className={cn(
          'overflow-hidden border-l-4 transition-all',
          config.borderColor,
          config.bgColor
        )}
      >
        <div className="p-4">
          {/* Line Items */}
          <div className="space-y-2">
            {items.map(lineItem => (
              <LineItemDisplay key={lineItem.lineItemId} lineItem={lineItem} />
            ))}
          </div>
        </div>

        {/* Justification Footer */}
        <JustificationFooter
          justification={item.justification}
          category={item.category}
          onEdit={() => setIsEditDialogOpen(true)}
          onDelete={handleDelete}
        />

        {/* Edit Justification Dialog */}
        <EditJustificationDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          justification={item.justification}
          onSave={handleSave}
        />
      </Card>
    );
  }

  // Matched Item Display - Three column layout (or single line for perfect match)
  // For perfect match, show as single line with just the items
  if (item.category === 'perfect_match') {
    return (
      <Card
        className={cn(
          'overflow-hidden border-l-4 transition-all',
          config.borderColor,
          config.bgColor
        )}
      >
        <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x">
          {/* Insurer Side - Left Half */}
          <div className="flex-1 flex flex-col p-3 gap-2 min-w-0 justify-center">
            <div className="space-y-2">
              {item.insurerItems.map(lineItem => (
                <LineItemDisplay key={lineItem.lineItemId} lineItem={lineItem} />
              ))}
              {item.insurerItems.length > 1 && (
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                  <span className="text-xs font-semibold text-muted-foreground">Subtotal:</span>
                  <span className="text-xs font-bold">
                    {formatCents(item.insurerItems.reduce((sum, li) => sum + li.rcvCents, 0))}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Contractor Side - Right Half */}
          <div className="flex-1 flex flex-col p-3 gap-2 min-w-0 justify-center">
            <div className="space-y-2">
              {item.contractorItems.map(lineItem => (
                <LineItemDisplay key={lineItem.lineItemId} lineItem={lineItem} />
              ))}
              {item.contractorItems.length > 1 && (
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                  <span className="text-xs font-semibold text-muted-foreground">Subtotal:</span>
                  <span className="text-xs font-bold">
                    {formatCents(item.contractorItems.reduce((sum, li) => sum + li.rcvCents, 0))}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Justification Footer */}
        <JustificationFooter
          justification={item.justification}
          category={item.category}
          onEdit={() => setIsEditDialogOpen(true)}
          onDelete={handleDelete}
        />

        {/* Edit Justification Dialog */}
        <EditJustificationDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          justification={item.justification}
          onSave={handleSave}
        />
      </Card>
    );
  }

  // Three column layout for other matched items
  return (
    <Card
      className={cn(
        'overflow-hidden border-l-4 transition-all',
        config.borderColor,
        config.bgColor
      )}
    >
      <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x">
        
        {/* Insurer Side - Left */}
        <div className="flex-1 flex flex-col p-3 gap-2 min-w-0 justify-center">
          <div className="space-y-2">
            {item.insurerItems.length > 0 ? (
              <>
                {item.insurerItems.map(lineItem => (
                  <LineItemDisplay key={lineItem.lineItemId} lineItem={lineItem} />
                ))}
                {item.insurerItems.length > 1 && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <span className="text-xs font-semibold text-muted-foreground">Subtotal:</span>
                    <span className="text-xs font-bold">
                      {formatCents(item.insurerItems.reduce((sum, li) => sum + li.rcvCents, 0))}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="h-full min-h-[48px] flex items-center justify-center border border-dashed rounded text-xs text-muted-foreground bg-slate-50">
                No Insurer Items
              </div>
            )}
          </div>
        </div>

        {/* Meta & Actions - Center */}
        <div className="w-full md:w-40 bg-slate-50/50 dark:bg-slate-950/50 p-3 flex flex-row md:flex-col items-center justify-between md:justify-center gap-3 shrink-0">
          <div className="flex flex-col items-center gap-1 w-full">
            {/* Show badge for categories */}
            <Badge variant="outline" className={cn(
              "text-[10px] whitespace-nowrap pointer-events-none",
              item.category === 'unmatched_contractor' && "bg-red-100 text-red-800 border-red-200",
              item.category === 'underscoped' && "bg-orange-100 text-orange-800 border-orange-200",
              item.category === 'overscoped' && "bg-purple-100 text-purple-800 border-purple-200",
              item.category === 'unmatched_insurer' && "bg-blue-100 text-blue-800 border-blue-200"
            )}>
              {config.label}
            </Badge>

            {/* Show price difference based on category */}
            {item.category === 'underscoped' && (
              <div className="text-xs font-bold mt-1 text-orange-600 dark:text-orange-400">
                +{formatCents(diffCents)}
              </div>
            )}

            {item.category === 'overscoped' && (
              <div className="text-xs font-bold mt-1 text-purple-600 dark:text-purple-400">
                +{formatCents(Math.abs(diffCents))}
              </div>
            )}
          </div>
        </div>

        {/* Contractor Side - Right */}
        <div className="flex-1 flex flex-col p-3 gap-2 min-w-0 justify-center">
          <div className="space-y-2">
            {item.contractorItems.length > 0 ? (
              <>
                {item.contractorItems.map(lineItem => (
                  <LineItemDisplay key={lineItem.lineItemId} lineItem={lineItem} />
                ))}
                {item.contractorItems.length > 1 && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <span className="text-xs font-semibold text-muted-foreground">Subtotal:</span>
                    <span className="text-xs font-bold">
                      {formatCents(item.contractorItems.reduce((sum, li) => sum + li.rcvCents, 0))}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="h-full min-h-[48px] flex items-center justify-center border border-dashed rounded text-xs text-muted-foreground bg-blue-50/50">
                No Contractor Items
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Justification Footer */}
      <JustificationFooter
        justification={item.justification}
        category={item.category}
        onEdit={() => setIsEditDialogOpen(true)}
        onDelete={handleDelete}
      />

      {/* Edit Justification Dialog */}
      <EditJustificationDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        justification={item.justification}
        onSave={handleSave}
      />
    </Card>
  );
}
